/**
 * Queries used by the subscription details webview (in-app cancellation, etc.)
 */

import { gql } from '@apollo/client';

/**
 * Explicitly query only for the editBillingInfoUrl field.
 * This field results in a Recurly API call so only call it when necessary.
 */
export const currentUserEditBillingInfoUrl = gql`
  query ECOMMERCE_currentUserEditBillingInfoUrl {
    currentUser {
      id
      editBillingInfoUrl
    }
  }
`;

/**
 * Create a Zendesk ticket requesting cancellation.
 */
export const createZendeskCancellationTicket = gql`
  mutation ECOMMERCE_createZendeskCancellationTicket($input: CreateZendeskCancellationTicketInput!) {
    createZendeskCancellationTicket(input: $input) {
      ticketCreated
    }
  }
`;

/**
 * Create a Zendesk ticket requesting replacement.
 */
export const createZendeskReplacementTicket = gql`
  mutation ECOMMERCE_createZendeskReplacementTicket($input: CreateZendeskReplacementTicketInput!) {
    createZendeskReplacementTicket(input: $input) {
      ticketCreated
    }
  }
`;

export const createZendeskTroubleshootingTicket = gql`
  mutation ECOMMERCE_createZendeskTroubleshootingTicket($input: CreateZendeskTroubleshootingTicketInput!) {
    createZendeskTroubleshootingTicket(input: $input) {
      ticketCreated
    }
  }
`;

/**
 * Find all pets with active subscriptions, which is used to selectively show the multidog discount retention page.
 */
export const petsWithActiveSubscriptionQuery = gql`
  query ECOMMERCE_petsWithActiveSubscription {
    petsWithActiveSubscription {
      id
      name
      photos {
        first {
          image {
            fullSize
          }
        }
      }
      device {
        id
        moduleId
        currentCollarModel
        subscription {
          id
          currentTermEndsAt
          subscriptionOption {
            name
            priceInCents
            billingCadence
            displayedRate
            displayedRateUnit
            renewalYears
            renewalMonths
            billingPeriodsPerTerm
            sku
          }
        }
        subscriptionAccessType
        hasSubscriptionOverride
      }
    }
  }
`;

/**
 * Apply multidog discount to current user.
 */
export const applyMultidogDiscountMutation = gql`
  mutation ECOMMERCE_applyMultidogDiscount {
    applyMultidogDiscount {
      success
    }
  }
`;

/**
 * Check if user already has multidog discount.
 */
export const hasMultidogDiscountQuery = gql`
  query ECOMMERCE_hasMultidogDiscount {
    currentUser {
      id
      hasMultidogDiscount
    }
  }
`;

/**
 * Claim a free month for a device.
 */
export const claimFreeMonthMutation = gql`
  mutation ECOMMERCE_claimFreeMonth($input: ClaimFreeMonthInput!) {
    claimFreeMonth(input: $input) {
      success
    }
  }
`;

/**
 * Claim a discount during the click-to-cancel flow.
 */
export const claimClickToCancelDiscountMutation = gql`
  mutation ECOMMERCE_claimClickToCancelDiscount($input: ClaimClickToCancelDiscountInput!) {
    claimClickToCancelDiscount(input: $input) {
      success
      reasonForFailure
    }
  }
`;

/**
 * Claim the cancel deterrent discount for free trial users.
 */
export const claimFreeTrialCancelDeterrentMutation = gql`
  mutation ECOMMERCE_claimFreeTrialCancelDeterrent($input: ClaimFreeTrialCancelDeterrentInput!) {
    claimFreeTrialCancelDeterrent(input: $input) {
      success
      reasonForFailure
    }
  }
`;

/**
 * Downgrade an S2 device to monthly plan.
 */
export const downgradeS2Mutation = gql`
  mutation ECOMMERCE_downgradeS2($input: DowngradeSeries2Input!) {
    downgradeSeries2(input: $input) {
      success
    }
  }
`;

/**
 * Get all plans S3 user can downgrade to.
 */
export const getDowngradeablePlans = gql`
  query ECOMMERCE_getAvailableS3DowngradeableSubscriptionOptions(
    $input: AvailableS3DowngradeSubscriptionOptionsInput!
  ) {
    getAvailableS3DowngradeableSubscriptionOptions(input: $input) {
      subscriptionOptions {
        sku
        name
        priceInCents
        pricingInCents {
          USD
          CAD
        }
        displayedRate
        displayedRateUnit
        billingCadence
        renewalMonths
        billingPeriodsPerTerm
        supportPlus
        valueLabel
        savings
        fullRate
        fullRateUnit
        featured
        renewalYears
        activationFeeInCents
        activationFeePricingInCents {
          USD
          CAD
        }
        upsellOnly
        buyItMembership
      }
    }
  }
`;

/**
 * Downgrade an S3 device to requested plan.
 */
export const downgradeS3Mutation = gql`
  mutation ECOMMERCE_downgradeS3($input: DowngradeSeries3Input!) {
    downgradeSeries3(input: $input) {
      success
    }
  }
`;

/**
 * Renew subscription for device.
 */
export const renewSubscriptionMutation = gql`
  mutation ECOMMERCE_renewSubscription($input: RenewSubscriptionInput!) {
    renewSubscription(input: $input) {
      success
    }
  }
`;

/**
 * Cancel supplement subscription.
 */
export const cancelSubscriptionMutation = gql`
  mutation ECOMMERCE_cancelSupplementSubscription($input: CancelSupplementSubscriptionInput!) {
    cancelSupplementSubscription(input: $input) {
      success
      reasonForFailure
    }
  }
`;

/**
 * Update supplement subscription.
 */
export const updateSubscriptionMutation = gql`
  mutation ECOMMERCE_updateSupplementSubscription($input: UpdateSupplementSubscriptionInput!) {
    updateSupplementSubscription(input: $input) {
      success
      reasonForFailure
    }
  }
`;

export const getMultipleSupplementSubscriptionsPricingQuery = gql`
  query ECOMMERCE_getMultipleSupplementSubscriptionsPricing($input: GetMultipleSupplementSubscriptionsPricingInput!) {
    getMultipleSupplementSubscriptionsPricing(input: $input) {
      subtotalInCents
      taxInCents
      totalInCents
    }
  }
`;

export const addMultipleSupplementSubscriptionsMutation = gql`
  mutation ECOMMERCE_addMultipleSupplementSubscriptions($input: AddMultipleSupplementSubscriptionsInput!) {
    addMultipleSupplementSubscriptions(input: $input) {
      success
      reasonForFailure
    }
  }
`;

/**
 * Update next ship date for supplements subscription.
 */
export const setSupplementNextShipDateMutation = gql`
  mutation ECOMMERCE_setSupplementNextShipDate($input: SetNextShipmentDateInput!) {
    setSupplementNextShipDate(input: $input) {
      success
      reasonForFailure
    }
  }
`;

/**
 * Set supplement subscription shipping address.
 */
export const setSupplementShippingAddressMutation = gql`
  mutation ECOMMERCE_setSupplementShippingAddress($input: SetShippingAddressInput!) {
    setSupplementShippingAddress(input: $input) {
      success
      reasonForFailure
    }
  }
`;

/**
 * Claim supplement cancellation deterrent to get 33% off next shipment.
 */
export const claimSupplementDiscountDeterrentMutation = gql`
  mutation ECOMMERCE_claimSupplementDiscount($input: ClaimSupplementDiscountInput!) {
    claimSupplementDiscount(input: $input) {
      success
      reasonForFailure
    }
  }
`;

/**
 * Claim monthly special offer cancellation deterrent to get 50% off next two months.
 */
export const claimTwoMonthsHalfOffMutation = gql`
  mutation ECOMMERCE_claimTwoMonthsHalfOffDiscount($input: ClaimTwoMonthsHalfOffInput!) {
    claimTwoMonthsHalfOff(input: $input) {
      success
      reasonForFailure
    }
  }
`;

/**
 * Pause membership for one month
 */
export const pauseMembershipMutation = gql`
  mutation ECOMMERCE_pauseMembership($input: PauseMembershipInput!) {
    pauseMembership(input: $input) {
      success
      reasonForFailure
    }
  }
`;

/**
 * Resume paused membership
 */
export const resumeMembershipMutation = gql`
  mutation ECOMMERCE_resumeMembership($input: ResumeMembershipInput!) {
    resumeMembership(input: $input) {
      success
      reasonForFailure
    }
  }
`;

export const cancelMembershipMutation = gql`
  mutation ECOMMERCE_cancelMembership($input: CancelMembershipInput!) {
    cancelMembership(input: $input) {
      success
      reasonForFailure
    }
  }
`;

/**
 * Cancel third party membership
 */
export const cancelThirdPartyMembershipMutation = gql`
  mutation ECOMMERCE_cancelThirdPartyMembership($input: CancelThirdPartySubscriptionInput!) {
    cancelThirdPartySubscription(input: $input) {
      success
      reasonForFailure
    }
  }
`;

/**
 * Retrieve all the subscriptions for the current user. This makes a Recurly call so only use when necessary.
 */
export const allSubscriptionsForCurrentUserQuery = gql`
  query ECOMMERCE_allSubscriptionsForCurrentUser {
    currentUser {
      email
      editBillingInfoUrl
      userHouseholds {
        householdRole
        household {
          pets {
            id
            name
            species
            photos {
              first {
                image {
                  fullSize
                }
              }
            }
            device {
              moduleId
              subscriptionId
              currentCollarModelLongName
              subscription {
                name
                code
                billingEmail
                isCanceled
                isPaused
                hasFreeTrialPlan
                isInTrialPeriod
                currentTermEndsAt
                expiresAt
                pausedAt
                currency
                unitAmountInCents
                subscriptionOption {
                  sku
                  priceInCents
                  renewalMonths
                  activationFeeInCents
                }
                nextRenewalPreview {
                  totalInCents
                }
                pendingChanges {
                  subscriptionOption {
                    priceInCents
                    renewalMonths
                  }
                }
                lastPaymentFailed
              }
              cxPendingAction {
                category
              }
            }
          }
        }
      }
    }
  }
`;

export const changeMembershipPlanEndOfTermMutation = gql`
  mutation ECOMMERCE_changeMembershipPlanEndOfTerm($input: ChangeMembershipPlanEndOfTermInput!) {
    changeMembershipPlanEndOfTerm(input: $input) {
      success
      reasonForFailure
    }
  }
`;

export const convertFreeTrialMutation = gql`
  mutation ECOMMERCE_convertFreeTrial($input: ConvertFreeTrialInput!) {
    convertFreeTrial(input: $input) {
      success
      reasonForFailure
    }
  }
`;
